import i18next from "i18next";
import { computed, type Ref } from "vue";
import { SINGLE_MODIFIERS_GROUP_ID, SINGLE_MODIFIERS_GROUP_NAME } from "#/constants/modifiers";
import { getLocaleName } from "#/utils/i18next";
import { applySelectedSizeToModifier, getModifierPriceForDishSize } from "#/utils/syncModifierWithSize";

export interface FlatModifierItem {
	group: any;
	groupIndex: number;
	modifier: any;
}

export interface ComboSelection {
	product: any;
	size: any;
	modifiers: any[];
	groupModifiers: any[];
}

const modifierHasSize = (modifier: any, selectedSizeId: string | null): boolean => {
	if (!selectedSizeId) return true;
	if (!modifier.sizes || !Array.isArray(modifier.sizes) || modifier.sizes.length === 0) return true;
	return modifier.sizes.some((size: any) => size.id === selectedSizeId);
};

const getModifierPrice = (modifier: any, size: any): number => getModifierPriceForDishSize(modifier, size);

const buildProductGroups = (product: any): any[] => {
	if (!product) return [];
	const groups: any[] = [];
	if (product.modifiers?.length > 0) {
		groups.push({
			id: SINGLE_MODIFIERS_GROUP_ID,
			name: SINGLE_MODIFIERS_GROUP_NAME,
			group_modifiers: product.modifiers,
			max_amount: Math.max(
				1,
				product.modifiers.reduce((sum: number, m: any) => sum + (m.max_amount || 0), 0),
			),
			min_amount: product.modifiers.reduce((sum: number, m: any) => sum + (m.min_amount || 0), 0),
			required: false,
		});
	}
	for (const g of product.group_modifiers ?? []) groups.push(g);
	return groups;
};

const groupCapacity = (group: any, product: any): number => {
	if (!group) return 0;
	if (group.id === SINGLE_MODIFIERS_GROUP_ID) {
		const pm = product?.modifiers ?? [];
		if (pm.length > 0) return pm.reduce((sum: number, m: any) => sum + (m.max_amount ?? 0), 0);
	}
	const own = group.max_amount ?? 0;
	if (own > 0) return own;
	return (group.group_modifiers ?? []).reduce((sum: number, m: any) => sum + (m.max_amount ?? 0), 0);
};

const pricedModifier = (sourceMod: any, amount: number, size: any) => {
	const sized = applySelectedSizeToModifier({ ...sourceMod, amount }, size);
	const price = getModifierPrice(sized, size);
	return { ...sized, amount, price, old_price: price };
};

const writeModifier = (selection: ComboSelection, group: any, sourceMod: any, amount: number, size: any) => {
	if (amount <= 0) return;
	const final = pricedModifier(sourceMod, amount, size);
	if (group.id === SINGLE_MODIFIERS_GROUP_ID) {
		const i = selection.modifiers.findIndex((m) => m.id === sourceMod.id);
		if (i !== -1) selection.modifiers[i] = final;
		else selection.modifiers.push(final);
		return;
	}
	let g = selection.groupModifiers.find((x: any) => (x.id || x.group) === group.id);
	if (!g) {
		g = { ...group, modifiers: [] };
		selection.groupModifiers.push(g);
	}
	const i = g.modifiers.findIndex((m: any) => m.id === sourceMod.id);
	if (i !== -1) g.modifiers[i] = final;
	else g.modifiers.push(final);
};

const removeModifier = (selection: ComboSelection, group: any, modifierId: string) => {
	if (group.id === SINGLE_MODIFIERS_GROUP_ID) {
		selection.modifiers = selection.modifiers.filter((m) => m.id !== modifierId);
		return;
	}
	const g = selection.groupModifiers.find((x: any) => (x.id || x.group) === group.id);
	if (g) g.modifiers = g.modifiers.filter((m: any) => m.id !== modifierId);
};

export const createComboSelection = (product: any, size: any = null): ComboSelection => {
	const selection: ComboSelection = { product, size: size ?? null, modifiers: [], groupModifiers: [] };
	for (const group of buildProductGroups(product)) {
		for (const mod of group.group_modifiers ?? []) {
			if ((mod.default_amount ?? 0) > 0) writeModifier(selection, group, mod, mod.default_amount, selection.size);
		}
	}
	return selection;
};

export function useComboModifiers(currentSelection: Ref<ComboSelection | null>) {
	const product = computed(() => currentSelection.value?.product ?? null);
	const selectedSize = computed(() => currentSelection.value?.size ?? null);
	const groups = computed(() => buildProductGroups(product.value));

	const flatModifiers = computed<FlatModifierItem[]>(() => {
		const sid = selectedSize.value?.id || null;
		return groups.value.flatMap((group: any, index: number) =>
			(group.group_modifiers || [])
				.filter((modifier: any) => modifierHasSize(modifier, sid))
				.map((modifier: any) => ({ group, groupIndex: index, modifier })),
		);
	});

	const singleModifiers = computed(() =>
		flatModifiers.value.filter((item) => item.group?.id === SINGLE_MODIFIERS_GROUP_ID && (item.modifier?.max_amount ?? 0) > 0),
	);
	const groupModifiersFlat = computed(() => flatModifiers.value.filter((item) => item.group?.id !== SINGLE_MODIFIERS_GROUP_ID));

	const getModifierAmount = (groupId: string, modifierId: string): number => {
		const sel = currentSelection.value;
		if (!sel) return 0;
		if (groupId === SINGLE_MODIFIERS_GROUP_ID) return sel.modifiers.find((m) => m.id === modifierId)?.amount ?? 0;
		const g = sel.groupModifiers.find((x: any) => (x.id || x.group) === groupId);
		return g?.modifiers.find((m: any) => m.id === modifierId)?.amount ?? 0;
	};

	const modifierAmounts = computed<Record<string, number>>(() => {
		const amounts: Record<string, number> = {};
		flatModifiers.value.forEach((item) => {
			amounts[`${item.group.id}-${item.modifier.id}`] = getModifierAmount(item.group.id, item.modifier.id);
		});
		return amounts;
	});

	const groupAmounts = computed<Record<string, number>>(() =>
		flatModifiers.value.reduce(
			(amounts: Record<string, number>, item) => {
				const groupId = item.group.id;
				amounts[groupId] = (amounts[groupId] || 0) + getModifierAmount(item.group.id, item.modifier.id);
				return amounts;
			},
			{} as Record<string, number>,
		),
	);

	const getGroupCapacity = (group: any): number => groupCapacity(group, product.value);

	const singleModifiersGroupMax = computed(() => getGroupCapacity(groups.value.find((g: any) => g.id === SINGLE_MODIFIERS_GROUP_ID)));
	const singleModifiersGroupSelected = computed(() => groupAmounts.value[SINGLE_MODIFIERS_GROUP_ID] ?? 0);

	const groupedModifiers = computed(() => {
		const result: Record<string, { group: any; modifiers: any[]; maxAmount: number; selectedAmount: number }> = {};
		groupModifiersFlat.value.forEach((item) => {
			const groupId = item.group.id;
			if (!result[groupId]) {
				result[groupId] = { group: item.group, modifiers: [], maxAmount: getGroupCapacity(item.group), selectedAmount: 0 };
			}
			result[groupId].modifiers.push(item.modifier);
			result[groupId].selectedAmount = groupAmounts.value[groupId] ?? 0;
		});
		return Object.values(result);
	});

	const getMaxAmount = (own: any, group: any) => {
		if (own?.max_amount > 0) return own.max_amount;
		if (group?.max_amount > 0) return group.max_amount;
		return 0;
	};

	const getCurrentGroupAmount = (groupId: string, modifierId: string) => (groupAmounts.value[groupId] ?? 0) - getModifierAmount(groupId, modifierId);

	const buildModifierData = (modifier: any, groupId: string) => {
		const sized = applySelectedSizeToModifier(modifier, selectedSize.value);
		return {
			...sized,
			id: modifier.id,
			name: modifier.name,
			price: getModifierPrice(modifier, selectedSize.value),
			loc_name: modifier.loc_name,
			image_link: modifier.image_link || product.value?.image_link || "",
			tags: [],
			amount: getModifierAmount(groupId, modifier.id),
		};
	};

	const updateModifierAmount = (groupId: string, modifierId: string, requested: number) => {
		const sel = currentSelection.value;
		if (!sel) return;
		const group = groups.value.find((g: any) => g.id === groupId);
		if (!group) return;
		const sourceMod = (group.group_modifiers || []).find((m: any) => m.id === modifierId);

		const current = getModifierAmount(groupId, modifierId);
		const otherInGroup = (groupAmounts.value[groupId] ?? 0) - current;

		let amount = requested;
		const gMax = getGroupCapacity(group);
		if (gMax > 0 && amount > 0) {
			const allowed = gMax - otherInGroup;
			if (allowed <= 0) return;
			amount = Math.min(amount, allowed);
		}
		if (sourceMod?.max_amount > 0 && amount > 0 && amount > sourceMod.max_amount) amount = sourceMod.max_amount;

		if (amount <= 0) removeModifier(sel, group, modifierId);
		else if (sourceMod) writeModifier(sel, group, sourceMod, amount, selectedSize.value);
	};

	const makeAmountChangeHandler = (groupId: string, modifierId: string) => (newAmount: number) => updateModifierAmount(groupId, modifierId, newAmount);

	const addOneModifier = (group: any, modifier: any) => {
		const current = getModifierAmount(group.id, modifier.id);

		if (getGroupCapacity(group) === 1) {
			const sel = currentSelection.value;
			if (!sel || current > 0) return;
			for (const m of group.group_modifiers || []) {
				if (m.id !== modifier.id) removeModifier(sel, group, m.id);
			}
			updateModifierAmount(group.id, modifier.id, 1);
			return;
		}

		const maxAmount = modifier.max_amount ?? group.max_amount ?? 0;
		if (maxAmount > 0 && current >= maxAmount) return;
		updateModifierAmount(group.id, modifier.id, current + 1);
	};

	const handleModifierClick = (group: any, _index: number, modifier: any) => addOneModifier(group, modifier);
	const makeGroupedModifierAddToCartHandler = (groupedGroup: any, modifier: any) => () => addOneModifier(groupedGroup.group, modifier);

	const repriceForSize = (size: any) => {
		const sel = currentSelection.value;
		if (!sel) return;
		sel.modifiers = sel.modifiers.map((m) => pricedModifier(m, m.amount, size));
		sel.groupModifiers = sel.groupModifiers.map((g: any) => ({ ...g, modifiers: g.modifiers.map((m: any) => pricedModifier(m, m.amount, size)) }));
	};

	const validateGroups = (): { valid: boolean; errorText: string } => {
		for (const group of groups.value) {
			const name = getLocaleName(group, i18next.language);
			const total = groupAmounts.value[group.id] ?? 0;
			if (group.min_amount > 0 && total < group.min_amount) {
				return { valid: false, errorText: i18next.t("card.error.min-amount", { name, amount: group.min_amount }) };
			}
			for (const mod of group.group_modifiers || []) {
				if (mod.min_amount > 0 && getModifierAmount(group.id, mod.id) < mod.min_amount) {
					return { valid: false, errorText: i18next.t("card.error.required-modifiers", { name }) };
				}
			}
		}
		return { valid: true, errorText: "" };
	};

	return {
		modifierAmounts,
		flatModifiers,
		singleModifiers,
		groupAmounts,
		singleModifiersGroupMax,
		singleModifiersGroupSelected,
		groupedModifiers,
		getMaxAmount,
		getGroupCapacity,
		getCurrentGroupAmount,
		getModifierAmount,
		buildModifierData,
		makeAmountChangeHandler,
		handleModifierClick,
		makeGroupedModifierAddToCartHandler,
		repriceForSize,
		validateGroups,
	};
}
